// src/pages/Patisserie/PatisserieItemCard.jsx
import React from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import ProductImage from "./ProductImage";
import ProductTitle from "./ProductTitle";
import ProductDescription from "./ProductDescription";
import ProductPrice from "./ProductPrice";
import QuantityAndCart from "../../components/QuantityAndCart";
import ProductTags from "./ProductTags";
import { useCart } from "../../context/CartContext";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import { buildLineId } from "../../utils/lineId";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const PatisserieItemCard = ({
  item,
  quantity,
  setQuantity,
  isAvailable = true,
}) => {
  const theme = useTheme();
  const { addToCart } = useCart();
  const snackbar = useSnackbarAlert();

  // Canonical list price from product data
  const listUnit = Number(item?.orderConfig?.regular?.price || 0);
  const itemsPerSet = Number(item?.orderConfig?.regular?.itemsPerSet || 1);

  // Display-only discounted unit (from useDiscountedProducts -> item.display.unit)
  const displayUnit = Number(item?.display?.unit ?? listUnit);
  const isDiscountedForDisplay = displayUnit < listUnit;

  // Derive UI totals for the card
  const uiLineListTotal = round2(listUnit * quantity);
  const uiLineDisplayTotal = round2(displayUnit * quantity);

  const {
    _id,
    name,
    displayName,
    flavor,
    description,
    images,
    allergens = [],
    dietaryTags = [],
    ingredients = [],
    category,
    subcategory,
  } = item;

  const type = subcategory || category || item.type || "patisserie";

  // Build options/customizations that define uniqueness of this line
  const needsDisplayName = ![
    "cupcake",
    "cookie",
    "macaron",
    "cheesecakeSlice",
  ].includes((subcategory || "").toLowerCase());

  const customizations = [];
  if (flavor) {
    customizations.push({ key: "flavor", label: "Flavor", value: flavor });
  }

  const optionsForId = {
    flavor: flavor || null,
    size: null,
    color: null,
    customizations,
  };

  const lineId = buildLineId({
    productId: _id,
    type,
    options: optionsForId,
  });

  const handleAddToCart = async () => {
    const newItem = {
      lineId, // stable id used for update/remove
      productId: _id,
      type,
      category: category || "patisserie",
      subcategory,
      name,
      displayName: needsDisplayName ? displayName : null,
      image: item?.images?.regularOrderImage || item?.image || null,
      listPrice: listUnit,
      quantity,
      itemsPerSet,
      flavor: flavor ? { name: flavor } : null,
      size: null,
      color: null,
      customizations, // ✅ canonical plural
      customization: customizations, // ✅ keep singular for isSameCartItem until fully migrated
    };

    const result = await addToCart(newItem);

    if (result.ok && result.synced) {
      snackbar.success("Item added to cart");
    } else if (result.ok && !result.synced) {
      snackbar.warning("Added to cart (sync failed, retry later).");
    } else {
      snackbar.error("Failed to add item to cart. Try again");
    }

    // Analytics — use displayed unit (what user sees) x qty
    if (window.fbq) {
      window.fbq("track", "AddToCart", {
        content_name: displayName || name,
        content_ids: [_id],
        content_type: "product",
        value: uiLineDisplayTotal,
        currency: "CAD",
      });
    }

    if (window.gtag) {
      window.gtag("event", "add_to_cart", {
        currency: "CAD",
        value: uiLineDisplayTotal,
        items: [
          {
            item_id: _id,
            item_name: displayName || name,
            item_category: category || "patisserie",
            item_variant: flavor || undefined,
            price: displayUnit,
            quantity,
          },
        ],
      });
    }
  };

  return (
    <Box
      sx={{
        width: 320,
        minHeight: 520,
        borderRadius: 4,
        boxShadow: 3,
        p: 2,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        transition: "0.3s",
        "&:hover": { boxShadow: 6 },
      }}>
      <ProductImage
        image={images?.regularOrderImage}
        displayName={displayName}
        isDiscounted={isDiscountedForDisplay}
        discountPercent={item?.display?.percentOff}
      />

      <ProductTitle
        subcategory={subcategory}
        displayName={displayName}
        flavor={flavor}
      />

      <ProductDescription description={description} />

      <ProductPrice
        isDiscounted={isDiscountedForDisplay}
        currentUnit={displayUnit}
        listUnit={listUnit}
        suffix="/each"
      />

      {isAvailable ? (
        <QuantityAndCart
          quantity={quantity}
          setQuantity={setQuantity}
          itemsPerSet={itemsPerSet}
          onAddToCart={handleAddToCart}
        />
      ) : (
        <Box
          sx={{
            mb: 1,
            textAlign: "center",
            py: 1.2,
            px: 1,
            backgroundColor: "#fce4e4",
            border: "1px solid #f8bdbd",
            borderRadius: "20px",
          }}>
          <Typography variant="body2" color="error" fontWeight={600}>
            Temporarily Unavailable
          </Typography>
        </Box>
      )}

      <Divider sx={{ width: "100%", mb: 1, mt: 2 }} />

      <Box
        sx={{
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          overflow: "hidden",
        }}>
        <ProductTags
          allergens={allergens}
          dietaryTags={dietaryTags}
          ingredients={ingredients}
          theme={theme}
        />
      </Box>
    </Box>
  );
};

export default PatisserieItemCard;
